/**
 * The performance endpoint serves non-fatal warnings as code-prefixed
 * strings (`UNPRICED_POSITIONS: …`, `NAV_SERIES_VAULT_EXCLUDED: …`). The
 * code is backend vocabulary and must never reach an investor, but the
 * meaning must: a chart that silently covers fewer vaults than the position
 * count is exactly what these exist to prevent. Each known code becomes one
 * plain sentence; anything unrecognized is passed through unchanged rather
 * than dropped.
 */
const WARNING_COPY: Record<string, string> = {
  NAV_SERIES_VAULT_EXCLUDED:
    'Some of your vaults are missing from this chart because ROWA cannot yet read what currency they are denominated in. Their value is not in the line above.',
  UNPRICED_POSITIONS:
    'Some positions have no price yet, so they are not reflected in this chart.',
  STALE_FX:
    'Some currency conversions are older than usual, so values may shift once fresh rates arrive.',
  ROWA_POSITIONS_TRUNCATED:
    'You hold more positions than this response can return, so the chart covers only the ones included.'
};

interface PerformanceWarning {
  /** Reader-facing sentence. */
  message: string;
  /** Served detail (vault addresses and the like), kept for the drill-down. */
  detail?: string;
}

export function describePerformanceWarnings(
  warnings: readonly string[]
): PerformanceWarning[] {
  return warnings.map((warning) => {
    const separator = warning.indexOf(':');
    if (separator === -1) return { message: warning };
    const code = warning.slice(0, separator).trim();
    const detail = warning.slice(separator + 1).trim();
    const copy = WARNING_COPY[code];
    if (!copy) return { message: warning };
    return { message: copy, ...(detail ? { detail } : {}) };
  });
}
